import { LogOut, Power } from 'lucide-react';
import { useNavigate } from 'react-router';
import { BRAND } from '@shared/constants/brand';
import type { Participant } from '@shared/types/room';
import { AdSlot } from '@/components/AdSlot';
import { ParticipantList } from '@/components/ParticipantList';
import { ShareButtons } from '@/components/ShareButtons';
import { useRoomStore } from '@/stores/roomStore';

export function LobbyView() {
  const navigate = useNavigate();
  const state = useRoomStore((s) => s.state);
  const selfId = useRoomStore((s) => s.selfId);
  const send = useRoomStore((s) => s.send);
  const leave = useRoomStore((s) => s.leave);
  if (!state) return null;

  const isHost = state.hostId === selfId;
  const activeCount = state.participants.filter((p) => p.active).length;
  const inviteUrl = `${location.origin}/room/${state.id}`;
  const remaining = state.maxParticipants - activeCount;

  const handleKick = (p: Participant) => {
    if (confirm(`${p.nickname}님을 내보낼까요?`)) send({ type: 'kick', participantId: p.id });
  };
  const handleLeave = () => {
    if (confirm('방에서 나갈까요?')) {
      leave();
      navigate('/');
    }
  };
  const handleClose = () => {
    if (confirm('방을 종료하면 모든 참여자가 나가게 돼요. 종료할까요?')) send({ type: 'close' });
  };

  return (
    <div className="flex flex-col gap-4 animate-fade-up">
      <div className="card p-5 sm:p-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-primary-600">대기실</p>
        <h1 className="mt-1 text-2xl font-extrabold break-keep">{state.name}</h1>
        <p className="mt-2 text-sm text-charcoal-500">
          참여 인원 <span className="font-bold text-charcoal-900">{activeCount}</span> / {state.maxParticipants}명
        </p>

        <div className="mt-5">
          <ParticipantList participants={state.participants} maxParticipants={state.maxParticipants} selfId={selfId} canKick={isHost} onKick={handleKick} />
        </div>

        <p className="mt-5 text-center text-sm font-medium text-charcoal-700" aria-live="polite">
          {remaining > 0 ? `동료들을 기다리고 있어요! (${remaining}명 더 필요해요)` : '모두 모였어요! 곧 시작해요 🎉'}
        </p>
      </div>

      <div className="card p-5">
        <p className="mb-3 text-sm font-semibold">초대 링크를 공유하세요</p>
        <p className="mb-3 truncate rounded-xl bg-cream-100 px-3 py-2 font-mono text-xs text-charcoal-500" title={inviteUrl}>
          {inviteUrl}
        </p>
        <ShareButtons title={BRAND.name} text={`[${BRAND.name}] "${state.name}" 방에서 오늘 점심 메뉴를 함께 정해요!`} url={inviteUrl} copyLabel="초대 링크 복사" shareLabel="초대 링크 공유" />
      </div>

      <AdSlot name="lobby" />

      <div className="flex flex-wrap justify-center gap-2">
        <button type="button" onClick={handleLeave} className="btn-ghost btn-sm">
          <LogOut size={16} aria-hidden="true" />
          나가기
        </button>
        {isHost && (
          <button type="button" onClick={handleClose} className="btn-ghost btn-sm text-red-500 hover:text-red-600">
            <Power size={16} aria-hidden="true" />
            방 종료
          </button>
        )}
      </div>
    </div>
  );
}
